<template>
  <div>
    <div
      v-infinite-scroll="onLoadMore"
      :infinite-scroll-disabled="disableScroll"
      infinite-scroll-distance="100"
    >
      <div v-for="item in list" :key="item.id" class="mb-4">
        <v-card outlined class="pa-4 audit-log-card" @click="toggle(item.id)">
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="subtitle-2 grey--text text--darken-1">
                {{ formatDate(item.created_at) }}
                <span v-if="getCauserName(item)">
                  - added by
                  <strong class="black--text">{{ getCauserName(item) }}</strong>
                </span>
              </div>
              <div class="body-2 mt-1">
                {{ getDescription(item) }}
              </div>
              <div
                v-if="Object.keys(getContext(item)).length"
                class="caption mt-1"
              >
                <template v-for="(entry, field) in getContext(item)">
                  <a
                    v-if="entry.href"
                    :key="field"
                    :href="entry.href"
                    target="_blank"
                    class="mr-3 context-link"
                    @click.stop
                  >
                    {{ entry.label }}: {{ entry.value }}
                  </a>
                  <span
                    v-else
                    :key="field"
                    class="mr-3 grey--text text--darken-1"
                  >
                    {{ entry.label }}: {{ entry.value }}
                  </span>
                </template>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-chip v-if="item.subject_type" small outlined class="mr-2">
                {{ item.subject_type }}
              </v-chip>
              <v-chip
                v-if="item.event"
                small
                :color="eventColor(item.event)"
                text-color="white"
                class="mr-2"
              >
                {{ item.event }}
              </v-chip>
              <v-btn
                v-if="getEntityRoute(item)"
                x-small
                outlined
                color="primary"
                :href="getEntityHref(item)"
                target="_blank"
                @click.stop
              >
                View
              </v-btn>
            </div>
          </div>

          <!-- Expandable change details -->
          <v-expand-transition>
            <div v-if="expanded === item.id && hasChanges(item)" class="mt-4">
              <v-divider class="mb-3" />

              <v-simple-table dense class="changes-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(change, field) in getChanges(item)"
                      :key="field"
                    >
                      <td class="font-weight-medium">
                        {{ formatField(field) }}
                      </td>
                      <td class="grey--text text--darken-1">
                        {{ formatValue(change.old) }}
                      </td>
                      <td class="green--text text--darken-1">
                        {{ formatValue(change.new) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </div>

    <!-- Empty state -->
    <no-list
      v-if="!list.length && !loading"
      details="No audit log entries found"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import infiniteScroll from 'vue-infinite-scroll'
import NoList from '@/components/elements/NoList'
dayjs.extend(utc)

export default {
  name: 'AuditLogTable',

  directives: {
    infiniteScroll,
  },

  components: {
    NoList,
  },

  props: {
    list: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    listMeta: {
      type: Object,
      default: () => ({ current_page: 0, last_page: 1 }),
    },
  },

  data() {
    return {
      expanded: null,
      contextRouteMap: {
        user: { label: 'User', name: 'user.profile', param: 'id' },
        user_program: {
          label: 'User Program',
          name: 'user.program-history.details',
          subjectIdParam: 'programId',
          requiresUserId: true,
        },
        user_subscription: {
          label: 'User Subscription',
          name: 'user.membership-history.details',
          subjectIdParam: 'userSubscriptionId',
          requiresUserId: true,
        },
      },
      entityRouteMap: {
        User: { name: 'user.profile', param: 'id' },
        Program: { name: 'program.details', param: 'id' },
        Package: { name: 'plan.details', param: 'id' },
        Food: { name: 'form.food', param: 'id' },
        Workout: { name: 'workout.details', param: 'id' },
        Exercise: { name: 'exercise.details', param: 'id' },
        Video: { name: 'video.details', param: 'id' },
        Promo: { name: 'promos.details', param: 'id' },
        MealGuide: { name: 'form.meals.details', param: 'id' },
        Trainer: { name: 'trainer.details', param: 'id' },
        Challenge: { name: 'challenge.details', param: 'id' },
        Membership: { name: 'memberships.details', param: 'id' },
        Mindset: { name: 'mindset.details', param: 'id' },
        FoodSwap: { name: 'form.food-swap', param: 'id' },
        UserSubscription: {
          name: 'user.membership-history.details',
          subjectIdParam: 'userSubscriptionId',
          requiresUserId: true,
        },
        UserProgram: {
          name: 'user.program-history.details',
          subjectIdParam: 'programId',
          requiresUserId: true,
        },
      },
    }
  },

  computed: {
    disableScroll() {
      return (
        this.loading || this.listMeta.current_page >= this.listMeta.last_page
      )
    },
  },

  methods: {
    onLoadMore() {
      this.$emit('loadMore')
    },

    toggle(id) {
      this.expanded = this.expanded === id ? null : id
    },

    formatDate(date) {
      return dayjs.utc(date).format('DD - MM - YYYY, h:mm a')
    },

    pickName(person) {
      if (!person) return null
      const fullName = (person.full_name || '').trim()
      const name = (person.name || '').trim()
      return fullName || name || person.email || null
    },

    getCauserName(item) {
      return this.pickName(item.causer) || this.pickName(item.user)
    },

    getDescription(item) {
      if (item.activity) return item.activity

      const event = item.event || 'updated'
      const type = item.subject_type || 'record'
      return `${type} was ${event}`
    },

    eventColor(event) {
      const colors = {
        created: 'green',
        updated: 'blue',
        deleted: 'red',
      }
      return colors[event] || 'grey'
    },

    hasChanges(item) {
      if (!item.properties) return false
      return item.properties.old || item.properties.attributes
    },

    getChanges(item) {
      const changes = {}
      const oldValues = item.properties.old || {}
      const newValues = item.properties.attributes || {}

      const allFields = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ])

      allFields.forEach((field) => {
        const oldVal = oldValues[field]
        const newVal = newValues[field]
        if (oldVal !== newVal) {
          changes[field] = { old: oldVal, new: newVal }
        }
      })

      return changes
    },

    getContext(item) {
      const ctx = item.properties?.context || {}
      const userId = ctx.user?.id ?? null
      const result = {}

      Object.entries(ctx).forEach(([key, entry]) => {
        if (!entry || entry.id === null || entry.id === undefined) return
        const route = this.contextRouteMap[key]
        const name = entry.name
        result[key] = {
          value: name ? `${name} (#${entry.id})` : `#${entry.id}`,
          label: route?.label ?? this.formatField(key),
          href: this.buildContextHref(key, entry.id, userId),
        }
      })

      return result
    },

    buildContextHref(key, id, userId) {
      const route = this.contextRouteMap[key]
      if (!route) return null
      if (route.requiresUserId && !userId) return null

      const params = route.requiresUserId
        ? { id: userId, [route.subjectIdParam]: id }
        : { [route.param]: id }

      try {
        return this.$router.resolve({ name: route.name, params }).href
      } catch (e) {
        return null
      }
    },

    formatField(field) {
      return field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },

    isDateString(value) {
      if (typeof value !== 'string') return false
      return /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value)
    },

    formatValue(value) {
      if (value === null || value === undefined) return '-'
      if (this.isDateString(value)) {
        return dayjs.utc(value).format('DD-MM-YYYY, h:mm a')
      }
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },

    extractUserId(item) {
      const props = item.properties || {}
      const attrs = props.attributes || {}
      const old = props.old || {}
      return attrs.user_id || old.user_id || null
    },

    getEntityRoute(item) {
      if (!item.subject_type || !item.subject_id) return null
      if (item.event === 'deleted') return null
      const route = this.entityRouteMap[item.subject_type]
      if (!route) return null
      if (route.requiresUserId && !this.extractUserId(item)) return null
      return route
    },

    getEntityHref(item) {
      const route = this.getEntityRoute(item)
      if (!route) return null

      let params
      if (route.requiresUserId) {
        params = {
          id: this.extractUserId(item),
          [route.subjectIdParam]: item.subject_id,
        }
      } else {
        params = { [route.param]: item.subject_id }
      }

      const resolved = this.$router.resolve({
        name: route.name,
        params,
      })
      return resolved.href
    },
  },
}
</script>

<style scoped>
.audit-log-card {
  cursor: pointer;
  transition: background-color 0.2s;
}
.audit-log-card:hover {
  background-color: #fafafa;
}
.context-link {
  color: inherit;
  text-decoration: none;
}
.context-link:hover {
  text-decoration: underline;
}
.changes-table >>> th,
.changes-table >>> td {
  font-size: 12px !important;
  height: 28px !important;
}
</style>
